/**
 * "Enviar por correo" dialog for a purchase order (JCD-23). CSV always
 * attached; PDF optional via a checkbox. The recipient is remembered per
 * vendor (localStorage, see lib/poEmailRecipient) so a repeat send doesn't
 * require retyping the address. Building the actual CSV/PDF bytes is the
 * caller's job (onSend) — this component only collects the recipient +
 * include-PDF choice and reports success/failure.
 */
import { useState } from 'react';
import { Button, Callout, Text, TextInput, Title } from '@tremor/react';
import { getLastPoEmailRecipient, setLastPoEmailRecipient } from '../lib/poEmailRecipient';
import { t } from '../lib/i18n';
import Modal from './Modal';

interface Props {
  supId: string;
  onSend: (to: string, includePdf: boolean) => Promise<void>;
  onClose: () => void;
}

export default function SendPurchaseEmailModal({ supId, onSend, onClose }: Props) {
  const [to, setTo] = useState(() => getLastPoEmailRecipient(supId));
  const [includePdf, setIncludePdf] = useState(true);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const et = t.purchasing.purchases.email;

  const submit = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      setErr(et.invalidEmail);
      return;
    }
    setSending(true);
    setErr(null);
    try {
      await onSend(to, includePdf);
      setLastPoEmailRecipient(supId, to);
      setSent(true);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal onClose={onClose} ariaLabel={et.title} size="sm" panelClassName="p-5">
      <Title>{et.title}</Title>

      <label className="block mt-4 text-sm">
        <Text className="text-tremor-content text-xs mb-1">{et.to}</Text>
        <TextInput
          value={to}
          onValueChange={setTo}
          placeholder={et.toPlaceholder}
          disabled={sending || sent}
        />
      </label>

      <label className="flex items-center gap-2 text-sm mt-3 cursor-pointer">
        <input
          type="checkbox"
          checked={includePdf}
          onChange={(e) => setIncludePdf(e.target.checked)}
          disabled={sending || sent}
        />
        {et.includePdf}
      </label>

      {err ? <Callout title={t.common.error} color="red" className="mt-4">{err}</Callout> : null}
      {sent ? <Callout title={et.sent} color="emerald" className="mt-4" /> : null}

      <div className="mt-5 flex gap-2 justify-end">
        <Button variant="light" onClick={onClose} disabled={sending}>
          {sent ? t.common.close : et.cancel}
        </Button>
        {!sent && (
          <Button onClick={submit} loading={sending} disabled={sending || !to}>
            {et.send}
          </Button>
        )}
      </div>
    </Modal>
  );
}
